// Turns the roster into one email per address.
//
// Recipients are grouped by household, not by player: a parent registered for
// two juniors gets a single message covering both children — and the children
// are covered whether or not they carry an email of their own, because the
// household link says they belong to that address. This mirrors the public
// GET /api.php?lookup=<email> endpoint, which answers the same question from
// the same household link.
//
// Coaches are never named in these emails (club preference) — the schedule
// lists day, time, court and programme only.

import { normalizeHouseholdId } from "./households";
import { ALL_DAYS } from "./tiers";
import { getGroupSchedule, formatRange, parseTime } from "./time";

export const DEFAULT_SUBJECT = "Your {term} schedule — Kings Athletic Club";

export const DEFAULT_INTRO =
  "The {term} schedule is set. Here is where you'll be playing each week.";

export const DEFAULT_SIGNOFF =
  "See you on court. Reply to this email if anything doesn't look right.\n\n— Kings Athletic Club";

const dayIndex = day => {
  const i = ALL_DAYS.indexOf(day);
  return i === -1 ? ALL_DAYS.length : i;
};

const firstName = name => String(name || "").trim().split(/\s+/)[0] || "";

// Weekend sessions carry a `kind` and are not tied to player.groupId, so they
// are found by membership instead. Everything else is the player's own group.
function sessionsFor(player, groups) {
  const own = groups.filter(g => g.id === player.groupId);
  const extra = groups.filter(
    g => g.kind && g.id !== player.groupId && (g.playerIds || []).includes(player.id)
  );
  return [...own, ...extra]
    .map(g => {
      const { startTime, duration } = getGroupSchedule(g);
      return {
        id: g.id,
        day: g.day || "",
        startTime,
        duration,
        time: startTime && duration ? formatRange(startTime, duration) : startTime,
        court: g.court || "",
        tier: g.tier || "",
        kind: g.kind || "",
        playerIds: g.playerIds || [],
      };
    })
    .sort(
      (a, b) =>
        dayIndex(a.day) - dayIndex(b.day) ||
        (parseTime(a.startTime) ?? 0) - (parseTime(b.startTime) ?? 0)
    );
}

/**
 * Every player with their sessions resolved, in roster order — including
 * players with no email and players with no group. Both the mail-out and the
 * CSV export are built from this, so an export is a faithful preview of what
 * members will be told.
 */
export function buildPlayerSchedules(players = [], groups = []) {
  const names = Object.fromEntries(players.map(p => [p.id, p.name]));
  return players.map(player => ({
    id: player.id,
    name: player.name,
    email: String(player.email || "").trim(),
    classification: player.classification || "",
    sessions: sessionsFor(player, groups).map(s => ({
      ...s,
      groupmates: s.playerIds.map(id => names[id]).filter(n => n && n !== player.name),
    })),
  }));
}

/**
 * One row per distinct email address, in roster order.
 *
 * An address covers everyone the club can reach through it: the players who
 * gave that address, plus every other player in their household. A junior with
 * no address of their own is therefore told their times through a parent's
 * message, and where both parents registered separately, each of them gets the
 * whole family's schedule rather than only their own line.
 *
 * Households are matched on the canonical membership number, so "038" and "38"
 * are one family. Players with no household link fall back to the old
 * behaviour — they are covered by their own address only, which is why the
 * view warns about anyone left unreachable.
 *
 * Rows with sessionCount === 0 are still returned so the admin can see who
 * would get an empty message; the view leaves them unselected.
 */
export function buildRecipients(players = [], groups = []) {
  const householdOf = new Map(
    (players || []).map(p => [p.id, normalizeHouseholdId(p.householdId)])
  );
  const membersOf = new Map();  // householdId -> [playerId]
  householdOf.forEach((householdId, playerId) => {
    if (!householdId) return;
    if (!membersOf.has(householdId)) membersOf.set(householdId, []);
    membersOf.get(householdId).push(playerId);
  });

  const schedules = buildPlayerSchedules(players, groups);
  const byEmail = new Map();

  schedules.forEach(person => {
    if (!person.email) return;
    const key = person.email.toLowerCase();
    if (!byEmail.has(key)) byEmail.set(key, { email: person.email, ids: new Set() });
    const row = byEmail.get(key);
    row.ids.add(person.id);
    // The whole household this person belongs to, not just this person: that
    // is the point of the link. Families where one parent registered several
    // people at their own address keep working — those ids are already in.
    (membersOf.get(householdOf.get(person.id)) || []).forEach(id => row.ids.add(id));
  });

  return [...byEmail.values()].map(({ email, ids }) => {
    // Filtered from `schedules` rather than collected as we go, so the people
    // in a message stay in roster order however the household was reached.
    const people = schedules.filter(p => ids.has(p.id));
    return {
      email,
      people,
      sessionCount: people.reduce((n, p) => n + p.sessions.length, 0),
    };
  });
}

// Kings Athletic Club palette, Brand Identity Guide 2026. Hex literals rather
// than the Tailwind tokens because email HTML carries no stylesheet — every
// rule has to be inline. MUTED is the Brooklyn Blue tint the app uses for
// secondary text, so the mail matches the schedule page members see.
//
// MUTED is for labels and detail lines only — the court, the groupmates, the
// footer. Every sentence addressed to the member (greeting, intro, sign-off)
// is full BLUE: sat next to each other, two weights of the same hue read as a
// mistake rather than as hierarchy.
const BLUE = "#111333";   // Brooklyn Blue
const CREAM = "#FDF6E3";  // Chalk Line Cream
const RED = "#790000";    // Varsity Red
const GREY = "#D6D6D3";   // Locker Room Grey
const GREEN = "#1A3C24";  // Lucky Green
const MUTED = "#5A5C80";

// Stand-in for the guide's Times Now. Custom faces cannot be relied on in
// email, so this is the closest stack that is already installed everywhere.
//
// It is repeated on every text element rather than set once on <body> and
// inherited: Gmail lifts the message out of its <body> and drops that tag's
// styles, so anything relying on inheritance falls back to Gmail's own
// sans-serif and lands in the inbox in a different face from its neighbours.
const SERIF = "Georgia,'Times New Roman',Times,serif";

// Every sentence written to the member — greeting, intro, sign-off — is set
// from this one declaration. They were styled separately before and drifted
// apart in colour and size; sat in the same message, the difference reads as
// a mistake rather than as hierarchy. One constant means they can't diverge
// again without someone meaning it.
const PROSE = `font-family:${SERIF};font-size:16px;line-height:1.5;color:${BLUE};margin:0 0 12px;`;

const escapeHtml = str =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fill = (template, term) => String(template || "").replace(/\{term\}/g, term || "");

// --- Links and line breaks in the admin-written copy ------------------------
//
// The intro and sign-off are free text typed in the Email tab, so they can't
// contain HTML — everything is escaped before it reaches the message. To still
// allow a link, the copy is written in the one markup members' eyes never see:
//
//   [our court rules](https://kac.example/rules)   -> a link labelled "our court rules"
//   https://kac.example/rules                      -> linked as-is
//   tennis@kingsathletic.com                       -> a mailto: link
//
// Only http(s) and mailto targets are allowed through; anything else is left
// as literal text rather than becoming a javascript: link in someone's inbox.

const MD_LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
const BARE_URL = /(^|[\s(])((?:https?:\/\/|www\.)[^\s<)]*[^\s<).,;:!?])/gi;
const BARE_EMAIL = /(^|[\s(])([^\s@<]+@[^\s@<]+\.[a-z]{2,})/gi;

function safeHref(url) {
  const u = String(url).trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) return u;
  if (/^www\./i.test(u)) return `https://${u}`;
  if (/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(u)) return `mailto:${u}`;
  return null;
}

// Takes already-escaped HTML and returns it with anchors added. Each anchor is
// parked behind a placeholder as it is built so the bare-URL and bare-email
// passes can't reach inside an href and link it a second time.
function linkify(escaped) {
  const held = [];
  // NUL-delimited index: nothing typed into a textarea contains one, so
  // restoring the anchors at the end can't collide with the admin's own text.
  const hold = html => `\u0000${held.push(html) - 1}\u0000`;
  const anchor = (href, label) =>
    hold(`<a href="${href}" style="color:${RED};text-decoration:underline;">${label}</a>`);

  return escaped
    .replace(MD_LINK, (match, label, url) => {
      const href = safeHref(url);
      return href ? anchor(href, label) : match;
    })
    .replace(BARE_URL, (match, before, url) => {
      const href = safeHref(url);
      return href ? before + anchor(href, url) : match;
    })
    .replace(BARE_EMAIL, (match, before, address) => before + anchor(`mailto:${address}`, address))
    // eslint-disable-next-line no-control-regex -- the NUL marker above
    .replace(/\u0000(\d+)\u0000/g, (match, i) => held[i]);
}

/**
 * One <p> per blank-line-separated block, with single newlines kept as <br>.
 * What the admin types in the textarea is what lands in the inbox — HTML
 * collapses whitespace, so without this a two-paragraph intro arrives as one
 * run-on line.
 */
function richText(str, style) {
  return String(str || "")
    .split(/\n{2,}/)
    .filter(block => block.trim() !== "")
    .map(block => `<p style="${style}">${linkify(escapeHtml(block)).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// The plain-text alternative can't carry a link, so "[label](url)" becomes
// "label (url)" — both halves stay readable.
const unmarkLinks = str => String(str || "").replace(MD_LINK, "$1 ($2)");

// "Hi Kathleen," for a single person. An address covering several people gets
// no greeting line at all — naming one of two parents would be arbitrary, and
// a generic stand-in reads worse than opening straight into the message.
// Returns "" in that case, and callers drop the line rather than emit a blank.
function greeting(people) {
  const firsts = [...new Set(people.map(p => firstName(p.name)).filter(Boolean))];
  return firsts.length === 1 ? `Hi ${firsts[0]},` : "";
}

// What a session is called TO THE MEMBER. Never the tier: "Adult Beginner" /
// "Junior Beginner" is an internal placement grade, and telling someone their
// clinic level in writing reads as a judgement of them. The label only
// distinguishes the kind of session, which is what a member actually needs to
// recognise on their calendar. Tiers stay in the admin views and the CSV
// exports, which are staff-only.
const sessionLabel = session =>
  session.kind === "private" ? "Private lesson"
  : session.kind === "openplay" ? "Open play"
  : "Group instruction";

/**
 * Renders one recipient's message.
 * options: { term, subject, intro, signoff, includeGroupmates }
 */
export function renderEmail(recipient, options = {}) {
  const {
    term = "",
    subject = DEFAULT_SUBJECT,
    intro = DEFAULT_INTRO,
    signoff = DEFAULT_SIGNOFF,
    includeGroupmates = false,
  } = options;

  const introText = fill(intro, term);
  const signoffText = fill(signoff, term);
  const multiPerson = recipient.people.length > 1;
  const hello = greeting(recipient.people);

  const textParts = hello ? [hello, ""] : [];
  textParts.push(unmarkLinks(introText), "");
  recipient.people.forEach(person => {
    if (multiPerson) textParts.push(`${person.name}`, "");
    if (person.sessions.length === 0) {
      textParts.push("  Not placed in a group yet — we'll be in touch shortly.", "");
      return;
    }
    person.sessions.forEach(s => {
      textParts.push(`  ${s.day} · ${s.time}`);
      if (sessionLabel(s)) textParts.push(`  ${sessionLabel(s)}`);
      if (s.court) textParts.push(`  ${s.court}`);
      if (includeGroupmates && s.groupmates.length > 0) {
        textParts.push(`  With: ${s.groupmates.join(", ")}`);
      }
      textParts.push("");
    });
  });
  textParts.push(unmarkLinks(signoffText));

  const sessionHtml = s => `
        <tr>
          <td style="padding:14px 18px;border-bottom:1px solid ${GREY};">
            <div style="font-family:${SERIF};font-size:19px;color:${BLUE};">
              ${escapeHtml(s.day)}
              <span style="color:${RED};">${escapeHtml(s.time)}</span>
            </div>
            ${sessionLabel(s) ? `<div style="font-family:${SERIF};font-size:11px;letter-spacing:.09em;text-transform:uppercase;color:${s.kind === "openplay" ? GREEN : MUTED};margin-top:5px;">${escapeHtml(sessionLabel(s))}</div>` : ""}
            ${s.court ? `<div style="font-family:${SERIF};font-size:13px;color:${MUTED};margin-top:4px;">${escapeHtml(s.court)}</div>` : ""}
            ${includeGroupmates && s.groupmates.length > 0
              ? `<div style="font-family:${SERIF};font-size:13px;color:${MUTED};margin-top:6px;">With: ${escapeHtml(s.groupmates.join(", "))}</div>`
              : ""}
          </td>
        </tr>`;

  const personHtml = person => `
      ${multiPerson ? `<h2 style="font-family:${SERIF};font-size:17px;font-weight:normal;color:${BLUE};margin:26px 0 8px;">${escapeHtml(person.name)}</h2>` : ""}
      ${person.sessions.length === 0
        ? `<p style="font-family:${SERIF};font-size:14px;color:${MUTED};margin:0;">Not placed in a group yet — we'll be in touch shortly.</p>`
        : `<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border:1px solid ${GREY};border-collapse:collapse;background:#ffffff;">
             ${person.sessions.map(sessionHtml).join("")}
           </table>`}`;

  // Brand Identity Guide 2026. Email rules the design: no webfonts (Gmail
  // strips @font-face, and Hacky/Times Now aren't installed on anyone's
  // device), no SVG, no background images. So the wordmark is set in the
  // Georgia/Times serif stack that stands in for Times Now, and the K♣
  // secondary mark is built from real characters rather than an image —
  // it survives image-blocking, which is on by default in many clients.
  const html = `<!doctype html>
<html>
  <head>
    <!-- Declared here as well as on the MIME part: names, the en dash in
         every time range, and the em dash in the sign-off are non-ASCII, and
         a client that trusts the document over the header renders them as
         mojibake without this. -->
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
  </head>
  <body style="margin:0;padding:24px 12px;background:${CREAM};font-family:${SERIF};color:${BLUE};">
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" width="560" style="width:100%;max-width:560px;border-collapse:collapse;background:#ffffff;border:1px solid ${GREY};">
            <tr>
              <td bgcolor="${BLUE}" style="background:${BLUE};padding:22px 24px;">
                <span style="font-family:${SERIF};font-size:26px;color:${CREAM};">K<span style="font-size:16px;">&#9827;</span></span>
                <span style="font-family:${SERIF};font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:${CREAM};padding-left:12px;">Kings Athletic Club</span>
              </td>
            </tr>
            <tr>
              <td style="padding:28px 24px;font-family:${SERIF};color:${BLUE};">
                ${term ? `<h1 style="font-family:${SERIF};font-weight:normal;margin:0 0 22px;font-size:27px;line-height:1.2;color:${BLUE};">${escapeHtml(term)} schedule</h1>` : ""}
                ${hello ? `<p style="${PROSE}">${escapeHtml(hello)}</p>` : ""}
                <div style="margin:0 0 22px;">${richText(introText, PROSE)}</div>
                ${recipient.people.map(personHtml).join("")}
                <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;margin-top:26px;">
                  <tr><td height="1" bgcolor="${GREY}" style="background:${GREY};font-size:0;line-height:0;">&nbsp;</td></tr>
                </table>
                <div style="margin:18px 0 0;">${richText(signoffText, PROSE)}</div>
              </td>
            </tr>
          </table>
          <p style="font-family:${SERIF};font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:${MUTED};margin:16px 0 0;">Kings Athletic Club · Brooklyn</p>
        </td>
      </tr>
    </table>
  </body>
</html>`;

  return {
    to: recipient.email,
    subject: fill(subject, term),
    text: textParts.join("\n"),
    html,
  };
}
